/**
 * Proteus utils v2.3.1 — shared storefront runtime.
 *
 * Loaded by the "Proteus utils" app embed. Registers window.proteus with:
 *
 *   proteus.events
 *     .broadcast(name, detail)        document + window CustomEvent
 *     .on(name, callback) → unsub     subscribe on both targets
 *
 *   proteus.cart
 *     .current()                      last cached cart (or null)
 *     .fetch()                        /cart.js, cache, return
 *     .refresh()                      fetch + emit cart:refresh
 *     .add(items)                     POST /cart/add.js, refresh, emit
 *     .change(params)                 POST /cart/change.js, refresh, emit
 *     .find_lines(predicate)          filter cached cart.items
 *
 *     attribute queue (debounced, batched POST to /cart/update.js):
 *     .add_attributes(arr)
 *     .add_attribute(name, val, extra_pairs)
 *     .prep_attributes_from_dl(event_partial, key, val, prefix)
 *     .flush_attributes()
 *
 *   proteus.money.format(cents, opts)
 *
 *   proteus.get_cookie(name) / set_cookie(name, value, days)
 *   proteus.get_param(name, optSearch)
 *
 *   proteus.debounce(fn, wait) / throttle(fn, limit)
 *   proteus.request_anim_frame(cb)
 *   proteus.wait_until(test) → Promise
 *   proteus.wait_for_element(selector) → Promise
 *   proteus.observe_selector(selector, callback, options) → disconnect()
 *
 * Cart change detection:
 *   Patches window.fetch (primary) + XMLHttpRequest (fallback) to detect
 *   when any code on the page hits /cart/* — including theme JS and
 *   third-party scripts. Dispatches `proteus:cart-changed` (legacy name)
 *   AND `cart:refresh` (modern) with the cart in event.detail.
 *
 *   For /cart/add.js specifically, we wait 600ms before triggering our own
 *   /cart.js follow-up — if the theme fetches /cart.js itself in that
 *   window (the standard pattern), we use that response. No double fetch.
 *
 * Legacy window.* aliases preserved at bottom:
 *   setCookie, getCookie, getParam, waitForElement, waitUntil,
 *   observeSelector, requestAnimFrame, gtm_add_cartAttribute.
 */

(function () {
  if (window.proteus && window.proteus.__booted_v231) return;
  const proteus = window.proteus || {};
  proteus.__booted_v231 = true;

  /* ============================================================ events */

  proteus.events = {
    broadcast(name, detail) {
      try {
        const evt = new CustomEvent(name, {detail});
        document.dispatchEvent(evt);
        window.dispatchEvent(evt);
      } catch (e) {
        /* very old browsers — best effort */
      }
    },
    on(name, callback) {
      document.addEventListener(name, callback);
      window.addEventListener(name, callback);
      return () => {
        document.removeEventListener(name, callback);
        window.removeEventListener(name, callback);
      };
    },
  };

  /* =========================================================== cookies */

  proteus.get_cookie = (name) => {
    try {
      const value = "; " + document.cookie;
      const parts = value.split("; " + name + "=");
      if (parts.length === 2) return parts.pop().split(";").shift();
      return null;
    } catch (e) {
      console.error("[proteus] get_cookie failed:", e.message);
      return null;
    }
  };

  // Note: domain is intentionally left unset so the cookie scopes to the
  // current host. v2.05 had a literal `{{*Base URL}}` Liquid placeholder
  // that never got substituted — that's removed. If a subdomain-spanning
  // cookie is needed, pass a `domain` option in a future revision.
  proteus.set_cookie = (name, value, days) => {
    try {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      const expires = "expires=" + date.toUTCString();
      document.cookie = name + "=" + value + ";" + expires + ";path=/";
    } catch (e) {
      console.error("[proteus] set_cookie failed:", e.message);
    }
  };

  /* ======================================================== URL params */

  proteus.get_param = (name, opt_search) => {
    opt_search = opt_search || location.search;
    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    const regex = new RegExp("[\\?&]" + name + "=([^&#]*)");
    const results = regex.exec(opt_search);
    return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
  };

  /* ============================================================ timing */

  proteus.debounce = function (fn, wait) {
    let timer = null;
    return function () {
      const args = arguments;
      const ctx = this;
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(ctx, args), wait);
    };
  };

  proteus.throttle = function (fn, limit) {
    let inThrottle = false;
    return function () {
      if (inThrottle) return;
      fn.apply(this, arguments);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    };
  };

  proteus.request_anim_frame = (() => {
    if (window.requestAnimationFrame) return window.requestAnimationFrame.bind(window);
    if (window.webkitRequestAnimationFrame) return window.webkitRequestAnimationFrame.bind(window);
    if (window.mozRequestAnimationFrame) return window.mozRequestAnimationFrame.bind(window);
    return (callback) => window.setTimeout(callback, 1000 / 60);
  })();

  // Poll via rAF until test() returns truthy. Rejects after timeout_ms.
  // Default 5000ms matches the historical "5s give up" pattern.
  proteus.wait_until = (test, timeout_ms) => {
    if (timeout_ms == null) timeout_ms = 5000;
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const poll = () => {
        try {
          if (test()) return resolve();
        } catch (e) {
          return reject(e);
        }
        if (timeout_ms > 0 && Date.now() - start >= timeout_ms) {
          return reject(new Error("wait_until: timeout after " + timeout_ms + "ms"));
        }
        proteus.request_anim_frame(poll);
      };
      poll();
    });
  };

  // Resolve with the element when a selector match appears. Rejects after
  // timeout_ms. Pass 0 to wait forever (not recommended).
  proteus.wait_for_element = (selector, timeout_ms) => {
    if (timeout_ms == null) timeout_ms = 5000;
    return new Promise((resolve, reject) => {
      const found = document.querySelector(selector);
      if (found) return resolve(found);

      let timer = null;
      const cleanup = () => {
        obs.disconnect();
        if (timer) clearTimeout(timer);
      };
      const obs = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches(selector)) {
              cleanup();
              return resolve(node);
            }
            if (node.querySelector) {
              const inner = node.querySelector(selector);
              if (inner) {
                cleanup();
                return resolve(inner);
              }
            }
          }
        }
      });
      obs.observe(document.documentElement, {childList: true, subtree: true});

      if (timeout_ms > 0) {
        timer = setTimeout(() => {
          cleanup();
          reject(
            new Error(
              "wait_for_element: '" + selector + "' not found within " + timeout_ms + "ms",
            ),
          );
        }, timeout_ms);
      }
    });
  };

  // Observe the DOM for any element matching selector — fires callback once
  // per element. Returns a disconnect function.
  //
  // Three call forms (all backwards-compatible):
  //   observe_selector(selector, callback)                          // forever
  //   observe_selector(selector, callback, 10000)                   // shorthand: 10s timeout
  //   observe_selector(selector, callback, { once: true, timeout: 10000, onTimeout: fn })
  proteus.observe_selector = (selector, callback, options) => {
    // Shorthand: third arg is a number → treat as timeout_ms
    if (typeof options === "number") options = {timeout: options};
    options = options || {};

    const seen = new WeakSet();
    let obs = null;
    let timeout_timer = null;
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      if (obs) obs.disconnect();
      if (timeout_timer) clearTimeout(timeout_timer);
      obs = null;
      timeout_timer = null;
    };

    const process = (el) => {
      if (seen.has(el)) return false;
      seen.add(el);
      callback(el);
      if (options.once) {
        finish();
        return true;
      }
      return false;
    };

    const scan = (root) => {
      root = root || document;
      if (root.matches && root.matches(selector)) {
        if (process(root)) return true;
      }
      if (root.querySelectorAll) {
        const els = root.querySelectorAll(selector);
        for (let i = 0; i < els.length; i++) {
          if (process(els[i])) return true;
        }
      }
      return false;
    };

    scan();
    if (done) return finish;

    obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (scan(node)) return;
        }
      }
    });
    obs.observe(options.document || document, {childList: true, subtree: true});

    if (options.timeout && options.timeout > 0) {
      timeout_timer = setTimeout(() => {
        if (typeof options.onTimeout === "function") {
          try {
            options.onTimeout();
          } catch (e) {
            console.error("[proteus] observe_selector onTimeout threw:", e);
          }
        }
        finish();
      }, options.timeout);
    }

    return finish;
  };

  /* ============================================================= money */

  proteus.money = {
    // Format an amount-in-cents as currency.
    //
    // The "target" currency (what the result is displayed in) is resolved in
    // priority order: opts.currency → Shopify.currency.active (Markets) →
    // cart.currency → "USD".
    //
    // If opts.from is set and it differs from the target, we apply
    // Shopify.currency.rate to convert. The assumption: opts.from is the
    // shop's base currency (typical for merchant-authored values like
    // thresholds). Shopify exposes `rate` as base→active, which is exactly
    // what we need for from=base, target=active.
    format(amountCents, opts) {
      const o = opts || {};
      const cart = cachedCart;
      const shopify_currency = window.Shopify && window.Shopify.currency;
      const target =
        o.currency ||
        (shopify_currency && shopify_currency.active) ||
        (cart && cart.currency) ||
        "USD";

      let amount = Number(amountCents) / 100;
      if (
        o.from &&
        shopify_currency &&
        String(o.from).toUpperCase() !== String(target).toUpperCase()
      ) {
        const rate = Number(shopify_currency.rate) || 1;
        amount = amount * rate;
      }

      try {
        return new Intl.NumberFormat(o.locale || undefined, {
          style: "currency",
          currency: target,
          maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
        }).format(amount);
      } catch (e) {
        return "$" + amount.toFixed(2);
      }
    },

    // Apply Shopify.currency.rate to convert amount-in-cents (assumed shop
    // base) into the customer's active currency. Returns a plain Number in
    // major units — not formatted, not cents. Use this when you need the
    // converted value for arithmetic (e.g., comparing a threshold against
    // cart total in the customer's currency).
    //
    // If a custom rate is needed, pass opts.rate. Otherwise reads from
    // Shopify.currency.rate (defaults to 1 if not present).
    convert(amountCents, opts) {
      const o = opts || {};
      const shopify_currency = window.Shopify && window.Shopify.currency;
      const rate =
        o.rate != null
          ? Number(o.rate)
          : (shopify_currency && Number(shopify_currency.rate)) || 1;
      return (Number(amountCents) / 100) * rate;
    },
  };

  /* ============================================================== cart */
  // Cart state + API. The cache (cachedCart) is the single source of truth
  // across all proteus.cart.* calls and is updated by both the explicit
  // API (cart.add/change/refresh) AND the passive interceptor below.

  let cachedCart = null;
  let lastEmittedHash = ""; // hash of the last cart we emitted — suppresses identical re-emits
  let internalFetchInFlight = false; // true while OUR /cart.js is in flight
  let pendingAddTimer = null; // /cart/add.js → wait-for-theme-cart-fetch timer
  const ADD_WAIT_MS = 600; // how long to wait for theme's /cart.js after add

  // Material-state fingerprint. Two cart responses that map to the same items
  // + quantities + line prices produce the same hash, so we dedupe identical
  // emits regardless of how rapidly they arrive. Real cart changes always
  // produce a different hash → always emit.
  function hash_cart(cart) {
    if (!cart) return "";
    const lines = (cart.items || []).map(
      (i) => i.key + ":" + i.quantity + ":" + (i.final_line_price || 0),
    );
    return (cart.total_price || 0) + "|" + (cart.item_count || 0) + "|" + lines.join(",");
  }

  function emit_cart_changed(cart) {
    if (!cart || !cart.items) return;
    const h = hash_cart(cart);
    if (h === lastEmittedHash) return;
    lastEmittedHash = h;
    cachedCart = cart;
    proteus.events.broadcast("proteus:cart-changed", cart);
    proteus.events.broadcast("cart:refresh", {cart});
  }

  async function internal_fetch_cart() {
    internalFetchInFlight = true;
    try {
      const res = await window.fetch("/cart.js", {credentials: "same-origin"});
      const cart = await res.json();
      emit_cart_changed(cart);
      return cart;
    } catch (e) {
      console.error("[proteus] cart fetch failed", e);
      return cachedCart;
    } finally {
      internalFetchInFlight = false;
    }
  }

  proteus.cart = {
    current: () => cachedCart,
    fetch: () => internal_fetch_cart(),
    refresh: () => internal_fetch_cart(),

    async add(items) {
      const body = JSON.stringify({items: Array.isArray(items) ? items : [items]});
      const res = await window.fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {"Content-Type": "application/json"},
        body,
      });
      // The interceptor catches the /cart/add response and will handle the
      // wait-then-refresh dance. We don't double-refresh from here.
      return res.json();
    },

    async change(params) {
      const res = await window.fetch("/cart/change.js", {
        method: "POST",
        credentials: "same-origin",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(params),
      });
      // /cart/change.js returns the full cart — interceptor will emit.
      return res.json();
    },

    find_lines(predicate) {
      if (!cachedCart || !cachedCart.items) return [];
      return cachedCart.items.filter(predicate);
    },
  };

  /* ----------------------------------- cart attributes (debounced batch) */

  proteus.cart.attr_queue = [];
  proteus.cart.attr_flush_timer = null;
  proteus.cart.attr_debounce_ms = 2500;

  proteus.cart.add_attributes = (attrs_array) => {
    if (!Array.isArray(attrs_array) || !attrs_array.length) return;
    proteus.cart.attr_queue.push(...attrs_array);
    clearTimeout(proteus.cart.attr_flush_timer);
    proteus.cart.attr_flush_timer = setTimeout(() => {
      proteus.cart.flush_attributes();
    }, proteus.cart.attr_debounce_ms);
  };

  proteus.cart.flush_attributes = () => {
    if (!proteus.cart.attr_queue.length) return;

    // De-dupe by attribute key. Last write wins. Each entry is
    // `attributes[key]=value` (URL-encoded) so we split on the first `=`.
    const deduped = {};
    for (const pair of proteus.cart.attr_queue) {
      const eq_idx = pair.indexOf("=");
      const key = eq_idx === -1 ? pair : pair.substring(0, eq_idx);
      deduped[key] = pair;
    }
    const body = Object.values(deduped).join("&");

    // Reset BEFORE the fetch so new pushes during the in-flight POST
    // queue up for the NEXT flush rather than being lost.
    proteus.cart.attr_queue = [];
    proteus.cart.attr_flush_timer = null;

    fetch("/cart/update.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8"},
      body,
    })
      .then((res) => {
        if (!res.ok) throw new Error("cart/update.js response not ok");
        return res.json();
      })
      .then((cart) => {
        // /cart/update.js returns the full cart — interceptor will emit too,
        // but we set window.cart_attributes_updated for legacy callers.
        window.cart_attributes_updated = true;
        emit_cart_changed(cart);
      })
      .catch((err) => {
        console.error("[proteus] cart attribute flush failed:", err);
      });
  };

  // Add a single cart attribute. Optional `multiple_attr_array` accepts
  // additional `attributes[key]=value` strings to enqueue together.
  // FIXED from v2.05: previously this pushed the attribute twice (once
  // unencoded, once encoded) and then called add_attributes with the
  // entire queue, causing recursive duplication.
  proteus.cart.add_attribute = (attr_name, attr_val, multiple_attr_array) => {
    if (typeof attr_name !== "string" || !attr_name.length) return;
    const value = attr_val == null ? "" : String(attr_val);
    const pair =
      "attributes[" + encodeURIComponent(attr_name) + "]=" + encodeURIComponent(value);
    const batch = [pair];
    if (Array.isArray(multiple_attr_array) && multiple_attr_array.length) {
      batch.push(...multiple_attr_array);
    }
    proteus.cart.add_attributes(batch);
  };

  // Pull cart attributes off the dataLayer. Looks for entries whose `event`
  // includes `dl_event_partial`, reads `dl_entry[dl_key]` as the attribute
  // name and `dl_entry[dl_val]` as the value. Optionally prefixes the
  // attribute name. Same bug-fix as add_attribute — no longer self-recurses.
  proteus.cart.prep_attributes_from_dl = (
    dl_event_partial,
    dl_key,
    dl_val,
    attr_name_prefix,
  ) => {
    if (!dl_event_partial || !dl_key || !dl_val) return;
    if (!Array.isArray(window.dataLayer) || !window.dataLayer.length) return;
    attr_name_prefix = attr_name_prefix == null ? "proteus-experimentation" : attr_name_prefix;

    const batch = [];
    for (const entry of window.dataLayer) {
      if (!entry || !entry.event || !entry.event.includes(dl_event_partial)) continue;
      const raw_name = entry[dl_key];
      const raw_val = entry[dl_val];
      if (!raw_name) continue;
      const name = attr_name_prefix
        ? attr_name_prefix + "-" + String(raw_name).toLowerCase()
        : String(raw_name);
      const value = attr_name_prefix
        ? "[" + String(raw_name) + "]" + String(raw_val)
        : String(raw_val);
      batch.push(
        "attributes[" + encodeURIComponent(name) + "]=" + encodeURIComponent(value),
      );
    }
    if (batch.length) proteus.cart.add_attributes(batch);
  };

  // Legacy proteus.* aliases (call sites in older treatments expect these
  // at the top level of `proteus`, not under `proteus.cart`).
  proteus.add_cartAttributes = proteus.cart.add_attributes;
  proteus.flush_cartAttributes = proteus.cart.flush_attributes;
  proteus.add_cartAttribute = proteus.cart.add_attribute;
  proteus.prep_cartAttributes_from_dl = proteus.cart.prep_attributes_from_dl;

  /* =========================================== cart change interception */
  // Lean version of v2.05's pattern. Patches fetch (primary) + XHR (fallback).
  // On full-cart endpoints (/cart.js, /cart/update.js, /cart/change.js,
  // /cart/clear.js), the response IS the cart — use it directly.
  // On /cart/add.js, the response is just the added line item, so we wait
  // ADD_WAIT_MS for the theme to do its own /cart.js fetch (the conventional
  // theme pattern). If it does, that fetch's emit cancels our pending timer.
  // If not, we fetch /cart.js ourselves.
  //
  // The emit dedupe (lastEmitAt + EMIT_DEDUPE_MS) prevents the same cart
  // event firing twice when both fetch and XHR paths catch the same call,
  // or when fetch + theme + our internal handler all converge on /cart.js.

  const CART_FULL_RX = /\/cart\.js|\/cart\/(update|change|clear)\.js/;
  const CART_ADD_RX = /\/cart\/add\.js/;
  const CART_ANY_RX = /\/cart\.js|\/cart\/(add|update|change|clear)\.js/;

  function schedule_add_followup() {
    if (pendingAddTimer) return; // already waiting
    pendingAddTimer = setTimeout(() => {
      pendingAddTimer = null;
      // If the theme already gave us the post-add cart (via its own /cart.js
      // call), cancel_add_followup will have been invoked and we won't be here.
      // Otherwise we fetch /cart.js ourselves. The emit hash check will
      // suppress if (somehow) the cart hasn't actually changed.
      internal_fetch_cart();
    }, ADD_WAIT_MS);
  }

  function cancel_add_followup() {
    if (pendingAddTimer) {
      clearTimeout(pendingAddTimer);
      pendingAddTimer = null;
    }
  }

  function handle_response_for_url(url, response_clone) {
    if (!url) return;
    if (CART_FULL_RX.test(url)) {
      response_clone
        .json()
        .then((cart) => {
          cancel_add_followup(); // theme satisfied the pending follow-up
          emit_cart_changed(cart);
        })
        .catch(() => {});
      return;
    }
    if (CART_ADD_RX.test(url)) {
      // /cart/add.js response is the line item, not the cart. Wait briefly.
      schedule_add_followup();
    }
  }

  function url_from_fetch_args(args) {
    if (!args || !args[0]) return null;
    if (typeof args[0] === "string") return args[0];
    if (args[0].url) return args[0].url;
    return null;
  }

  // ---- fetch patch (primary) ----
  if (window.fetch && !window.__proteus_fetch_patched) {
    window.__proteus_fetch_patched = true;
    const original_fetch = window.fetch;
    window.fetch = function () {
      const args = arguments;
      const url = url_from_fetch_args(args);
      const promise = original_fetch.apply(this, args);
      // Skip our own internal /cart.js fetch — internal_fetch_cart emits
      // directly, no need to re-emit via the interceptor.
      if (internalFetchInFlight && url && /\/cart\.js/.test(url)) {
        return promise;
      }
      if (url && CART_ANY_RX.test(url)) {
        promise
          .then((response) => {
            try {
              handle_response_for_url(url, response.clone());
            } catch (e) {}
          })
          .catch(() => {});
      }
      return promise;
    };
  }

  // ---- XHR patch (fallback) ----
  // Some themes still use XMLHttpRequest for cart calls. We catch them too.
  // The emit-dedupe handles the case where fetch and XHR both fire for the
  // same logical operation (rare but possible if shims are layered).
  if (window.XMLHttpRequest && !window.__proteus_xhr_patched) {
    window.__proteus_xhr_patched = true;
    const original_open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (typeof url === "string" && CART_ANY_RX.test(url)) {
        this.addEventListener("load", function () {
          try {
            // Build a fake "response clone" with .json() that parses the text
            const text = this.responseText;
            const fake_response = {
              json: () => Promise.resolve(JSON.parse(text)),
            };
            handle_response_for_url(url, fake_response);
          } catch (e) {}
        });
      }
      return original_open.apply(this, arguments);
    };
  }

  /* =========================================================== aliases */
  // camelCase window.* names preserved for legacy treatment scripts.

  window.setCookie = proteus.set_cookie;
  window.getCookie = proteus.get_cookie;
  window.getParam = proteus.get_param;
  window.waitForElement = proteus.wait_for_element;
  window.waitUntil = proteus.wait_until;
  window.observeSelector = proteus.observe_selector;
  window.requestAnimFrame = function (cb) {
    return proteus.request_anim_frame(cb);
  };
  window.gtm_add_cartAttribute = proteus.cart.add_attribute;

  /* ============================================================== boot */

  // Prime the cart cache. internal_fetch_cart marks the internalFetchInFlight
  // flag so the fetch patch above ignores its own /cart.js fetch and we don't
  // emit on boot for no reason. Other events still emit normally.
  internal_fetch_cart();

  window.proteus = proteus;

  try {
    console.info("[proteus] utils v2.3.1 ready");
  } catch (e) {}
})();
